import React, { FC, FormEventHandler, ReactElement, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Control, Controller } from "react-hook-form";
import { Autocomplete } from "@mui/material";
import { MuiTelInput } from "mui-tel-input";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from "@components/base";
import { getTranslatedCountries } from "@helpers";

export type RegisterFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  companyId: string;
  promocode?: string;
  registrationCountry: string;
  password: string;
  repeatPassword: string;
  termsAndConditions: boolean;
};

export interface RegisterForm {
  onSubmit: FormEventHandler<HTMLFormElement>;
  control: Control<RegisterFormValues>;
  submitting: boolean;
  watch: (name: keyof RegisterFormValues) => string;
}

const RegisterForm: FC<RegisterForm> = ({
  onSubmit,
  control,
  submitting,
  watch,
}) => {
  const { t, i18n } = useTranslation();

  // Get translated country list based on current language
  const countryList = useMemo(() => {
    return getTranslatedCountries(i18n.language);
  }, [i18n.language]);

  return (
    <form onSubmit={onSubmit}>
      <Stack direction="column" rowGap={3}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <Controller
              control={control}
              name="firstName"
              render={({
                field: { onBlur, onChange, name },
                fieldState: { error },
              }): ReactElement => (
                <TextField
                  {...{ onBlur, onChange, name }}
                  error={Boolean(error)}
                  helperText={error?.message}
                  label={t("register.firstName")}
                  disabled={submitting}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              control={control}
              name="lastName"
              render={({
                field: { onBlur, onChange, name },
                fieldState: { error },
              }): ReactElement => (
                <TextField
                  {...{ onBlur, onChange, name }}
                  error={Boolean(error)}
                  helperText={error?.message}
                  label={t("register.lastName")}
                  disabled={submitting}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              control={control}
              name="email"
              render={({
                field: { onBlur, onChange, name },
                fieldState: { error },
              }): ReactElement => (
                <TextField
                  {...{ onBlur, onChange, name }}
                  error={Boolean(error)}
                  helperText={error?.message}
                  label={t("register.emailAddress")}
                  type="email"
                  disabled={submitting}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              control={control}
              name="phone"
              render={({ field, fieldState: { error } }) => (
                <MuiTelInput
                  {...field}
                  placeholder={t("settings.phone")}
                  defaultCountry="CZ"
                  error={Boolean(error)}
                  helperText={error?.message}
                  fullWidth
                  variant="outlined"
                />
              )}
            />
          </Grid>
        </Grid>

        <Divider />

        <Controller
          control={control}
          name="companyName"
          render={({
            field: { onBlur, onChange, name },
            fieldState: { error },
          }): ReactElement => (
            <TextField
              {...{ onBlur, onChange, name }}
              error={Boolean(error)}
              helperText={error?.message}
              label={t("register.companyName")}
              disabled={submitting}
              fullWidth
            />
          )}
        />

        <Controller
          control={control}
          name="registrationCountry"
          rules={{
            required: t("validation.country"),
          }}
          render={({
            field: { onBlur, onChange, value },
            fieldState: { error },
          }) => (
            <Autocomplete
              options={countryList}
              getOptionLabel={(option) => option.label}
              onChange={(event, newValue) => {
                onChange(newValue ? newValue.code : "");
              }}
              value={countryList.find((c) => c.code === value) || null}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderInput={(params) => (
                <TextField
                  {...params}
                  onBlur={onBlur}
                  error={Boolean(error)}
                  helperText={error?.message}
                  label={t("register.countryOfRegistration")}
                  disabled={submitting}
                />
              )}
              fullWidth
            />
          )}
        />
        <Controller
          control={control}
          name="companyId"
          render={({
            field: { onBlur, onChange, name },
            fieldState: { error },
          }): ReactElement => (
            <TextField
              {...{ onBlur, onChange, name }}
              error={Boolean(error)}
              helperText={error?.message}
              label={t("register.companyVAT")}
              disabled={submitting}
              fullWidth
            />
          )}
        />
        <Divider />
        <Grid container spacing={2}>
          <Grid size={6}>
            <Controller
              control={control}
              name="password"
              rules={{ required: "validation.password" }}
              render={({
                field: { onBlur, onChange, value, name },
                fieldState: { error },
              }) => (
                <TextField
                  {...{ onBlur, onChange, name, value }}
                  error={Boolean(error)}
                  helperText={error?.message}
                  label={t("register.password")}
                  type="password"
                  fullWidth
                  disabled={submitting}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              control={control}
              name="repeatPassword"
              rules={{
                required: t("validation.repeatPassword"),
                validate: (value) =>
                  value === watch("password") || t("validation.passwordsMustMatch"),
              }}
              render={({
                field: { onBlur, onChange, value, name },
                fieldState: { error },
              }) => (
                <TextField
                  {...{ onBlur, onChange, name, value }}
                  error={Boolean(error)}
                  helperText={error?.message}
                  label={t("register.repeatPassword")}
                  type="password"
                  fullWidth
                  disabled={submitting}
                />
              )}
            />
          </Grid>
        </Grid>
        <Controller
          control={control}
          name="promocode"
          render={({
            field: { onBlur, onChange, name },
            fieldState: { error },
          }): ReactElement => (
            <TextField
              {...{ onBlur, onChange, name }}
              error={Boolean(error)}
              helperText={error?.message}
              label={t("register.promocode")}
              disabled={submitting}
              fullWidth
            />
          )}
        />
        <Box mx={2}>
          <Controller
            control={control}
            name="termsAndConditions"
            render={({ field, fieldState: { error } }): ReactElement => (
              <FormControlLabel
                control={<Checkbox checked={field.value} {...field} />}
                label={
                  <Typography variant="body2" color={error ? "error" : "inherit"}>
                    <Trans
                      i18nKey="register.termsAndConditions"
                      components={[
                        <Link
                          href="https://www.greenometer.com/greenometer-platform-terms-of-use"
                          target="_blank"
                        />,
                      ]}
                    />
                  </Typography>
                }
              />
            )}
          />
        </Box>
        <Button
          size="medium"
          type="submit"
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {t("common.start")}
        </Button>
      </Stack>
    </form>
  );
};

export default RegisterForm;
